import React, { useState, useEffect, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Info, Loader2 } from 'lucide-react';
import GiftCardsComponent from '@/components/giftCards/GiftCards';
import GiftBanner from '@/components/giftCards/GiftBanner';
import GiftCardViewModal from '@/components/giftCards/GiftCardViewModal';
import GiftCardGuestBanner from '@/components/giftCards/GiftCardGuestBanner';
import GiftCardInfoCards from '@/components/giftCards/GiftCardInfoCards';
import GiftCardFAQ from '@/components/giftCards/GiftCardFAQ';
import SearchBar from '@/components/common/GlobalSearchModal/components/SearchBar';
import { searchGiftCard } from '@/services/giftCard/giftCardService';
import styles from './GiftCards.module.css';

/**
 * GiftCards Page
 * Full page displaying gift cards with header content and search bar
 */
const GiftCards = () => {
  const giftCardsRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const debounceTimerRef = useRef(null);

  // Check login status
  useEffect(() => {
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      setIsLoggedIn(!!token);
    };

    checkAuthStatus();
    window.addEventListener('storage', checkAuthStatus);
    return () => window.removeEventListener('storage', checkAuthStatus);
  }, []);

  const handleActivateClick = () => {
    // Trigger activation modal in GiftCards component
    if (giftCardsRef.current && giftCardsRef.current.openActivationModal) {
      giftCardsRef.current.openActivationModal();
    }
  };

  // Debounced search - only trigger API call when at least 3 characters
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedQuery = searchQuery.trim();

    // If empty, close popover and clear results
    if (!trimmedQuery) {
      setSearchResults(null);
      setIsOpen(false);
      setIsSearching(false);
      return;
    }

    // Only trigger API call if at least 3 characters
    if (trimmedQuery.length < 3) {
      setSearchResults(null);
      setIsSearching(false);
      // Keep popover open if user is typing (1-2 chars) to show message
      if (trimmedQuery.length > 0) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
      return;
    }

    // Open popover and start searching
    setIsSearching(true);
    setIsOpen(true);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = await searchGiftCard(trimmedQuery);
        if (response.success && response.giftCard) {
          // Transform API response to match component structure
          const transformedCard = {
            cardId: response.giftCard.id.toString(),
            title: response.giftCard.description || `Gift Card ${response.giftCard.uniqueKey}`,
            amount: response.giftCard.amount || 0,
            staticValue: response.giftCard.amount || 0,
            wagerRequirement: response.giftCard.wagerStatus ? response.giftCard.wagerTimes : 0,
            status: 'unclaimed',
            description: response.giftCard.description || '',
            expiryDate: response.giftCard.endDate || null,
            activationCode: response.giftCard.uniqueKey,
            requiresDeposit: response.giftCard.depositStatus || false,
            depositAmount: response.giftCard.depositAmount || 0,
            currentWager: 0,
            totalWager: (response.giftCard.amount || 0) * (response.giftCard.wagerTimes || 0),
            isActivated: false,
            activatedDate: null,
            // API specific fields
            periodDays: response.giftCard.periodDays,
            allUserStatus: response.giftCard.allUserStatus,
            isActive: response.giftCard.isActive,
            createdAt: response.giftCard.createdAt,
            updatedAt: response.giftCard.updatedAt,
          };
          setSearchResults(transformedCard);
        } else {
          setSearchResults(null);
        }
      } catch (error) {
        console.error('Error searching gift card:', error);
        setSearchResults(null);
        // Error is handled silently in search - user will see "No gift card found"
      } finally {
        setIsSearching(false);
      }
    }, 500); // 500ms debounce

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  const handleInfoClick = (e, card) => {
    e.stopPropagation();
    setSelectedCard(card);
    setShowInfoModal(true);
    setIsOpen(false);
  };

  const handleCardActivate = async (code) => {
    // After activation, refetch gift cards to get updated data
    if (giftCardsRef.current && giftCardsRef.current.refetchGiftCards) {
      setTimeout(() => {
        giftCardsRef.current.refetchGiftCards();
      }, 1000);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Open popover if user has typed at least 3 characters
    if (value.trim().length >= 3) {
      setIsOpen(true);
    } else if (value.trim().length === 0) {
      setIsOpen(false);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults(null);
    setIsOpen(false);
    setIsSearching(false);
  };

  return (
 <>
 {
  isLoggedIn ? (
    <>
       <div className={styles.pageContainer}>
      {/* Search Bar Section at Top */}
      <div className={styles.searchSection}>
        <div className={styles.searchSectionContent}>
          <div className={styles.searchSectionLeft}>
            <h2 className={styles.searchSectionTitle}>Find Your Gift Card</h2>
            <p className={styles.searchSectionDescription}>
              Search by activation code to view gift card details and requirements
            </p>
          </div>
          <div className={styles.searchSectionRight}>
            <Popover.Root 
              open={isOpen && (searchQuery.trim().length >= 3 || isSearching)} 
              onOpenChange={(open) => {
                // Only close if not searching and query is less than 3 chars
                if (!open && !isSearching && searchQuery.trim().length < 3) {
                  setIsOpen(false);
                }
              }}
            >
              <Popover.Trigger asChild>
                <div className={styles.searchWrapper}>
                  <SearchBar
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onClear={handleSearchClear}
                    placeholder="Enter gift card code (min 3 characters)..."
                  />
                </div>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content 
                  className={styles.popoverContent} 
                  sideOffset={5}
                  style={{ outline: 'none', border: 'none' }}
                >
                  {isSearching ? (
                    <div className={styles.searchLoading}>
                      <Loader2 className={styles.spinner} size={20} />
                      <span>Searching...</span>
                    </div>
                  ) : searchResults ? (
                    <div className={styles.searchResult}>
                      <div className={styles.searchResultItem}>
                        <div className={styles.searchResultLeft}>
                          <span className={styles.searchResultName}>
                            {searchResults.title || searchResults.activationCode}
                          </span>
                        </div>
                        <button
                          className={styles.searchResultInfoButton}
                          onClick={(e) => handleInfoClick(e, searchResults)}
                          aria-label="View gift card details"
                        >
                          <Info size={18} />
                        </button>
                      </div>
                    </div>
                  ) : searchQuery.trim().length >= 3 ? (
                    <div className={styles.searchNoResults}>
                      No gift card found with code "{searchQuery}"
                    </div>
                  ) : searchQuery.trim().length > 0 && searchQuery.trim().length < 3 ? (
                    <div className={styles.searchNoResults}>
                      Please enter at least 3 characters to search
                    </div>
                  ) : null}
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          </div>
        </div>
      </div>

      {/* Welcome Banner */}
      <GiftBanner onActivateClick={handleActivateClick} />

      {/* Gift Cards Component */}
      <GiftCardsComponent ref={giftCardsRef} />

      {/* View Modal */}
      {selectedCard && (
        <GiftCardViewModal
          isOpen={showInfoModal}
          onClose={() => {
            setShowInfoModal(false);
            setSelectedCard(null);
          }}
          card={selectedCard}
          onActivate={handleCardActivate}
        />
      )}
    </div>
    </>
  ) : (
    <div className={styles.guestContainer}>
      {/* Banner Section */}
      <GiftCardGuestBanner />
      
      {/* Info Cards Section */}
      <GiftCardInfoCards />
      
      {/* FAQ Section */}
      <GiftCardFAQ />
    </div>
  )
 }
 </>
  );
};

export default GiftCards;
